import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled, css } from '@compiled/react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import AlignTextCenterIcon from '@atlaskit/icon/core/align-text-center';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';

import { TitleAlignmentType, useTitleContentPropertiesForEditor } from '@confluence/content-topper';

import { AlignmentButton } from './AlignmentButton';

const leftAlignmentsButtonBoxXcss = xcss({
	paddingRight: 'space.050',
});
const dropdownContainerStyles = xcss({
	paddingRight: 'space.075',
	paddingLeft: 'space.075',
});

const dropdownMenuStyles = css({
	width: '50px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconStyles = styled.div<{
	isSelected: boolean;
}>({
	height: '32px',
	width: '32px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	border: ({ isSelected }) => (isSelected ? `1px solid ${token('color.border.selected')}` : 'none'),
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& button': {
		borderRadius: token('border.radius.050'),
		paddingInline: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'& button::before': {
		background: 'none',
	},
});

const i18n = defineMessages({
	leftAlignmentIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.left-alignment-icon.label',
		defaultMessage: 'Align Left',
		description: 'Label for the left alignment icon in floating toolbar above title',
	},
	centerAlignmentIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.center-alignment-icon.label',
		defaultMessage: 'Align Center',
		description: 'Label for the center alignment icon in floating toolbar above title',
	},
	dropdownMenuLeftAlignedIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.dropdown-menu-left-aligned-icon.label',
		defaultMessage: 'Left Aligned',
		description:
			'Label for the left alignment icon in alignment dropdown menu of the floating toolbar above title',
	},
	dropdownMenuCenterAlignedIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.dropdown-menu-center-aligned-icon.label',
		defaultMessage: 'Center Aligned',
		description:
			'Label for the center alignment icon in alignment dropdown menu of the floating toolbar above title',
	},
	dropdownMenuAlignmentMenuLabel: {
		id: 'title-area-content-components.toolbar-above-title.dropdown-menu-alignment-icon.label',
		defaultMessage: 'Select for alignment options',
		description: 'Label for the alignment dropdown menu in floating toolbar above title',
	},
});

export const AlignmentComponent = ({
	setIsMoreOptionsButtonFocused,
	alignmentButtonContainer,
	floatingToolbarButtonStyle,
	userOffline,
	VerticalLineWrapper,
	VerticalLine,
	createAnalyticsEvent,
	flags,
	contentType,
	alignmentButtonState,
	coverPictureState,
	contentId,
	leftPositionAtEmojiButtonInlineCss,
	setIsAlignmentDropdownOpen,
	isAlignmentDropdownOpen,
}) => {
	const intl = useIntl();
	const updateAlignmentState = alignmentButtonState?.toggleAlignmentButton;
	const updateCoverPictureWidthState = coverPictureState?.toggleCoverPictureWidthButton;

	const { updatePageTitleProperty, isUpdatingAlignment } = useTitleContentPropertiesForEditor({
		contentId,
		updateAlignmentState,
		updateCoverPictureWidthState,
		contentType,
		flags,
	});

	const createAlignmentButtonAnalytics = useCallback(
		(alignment: string) => {
			const alignmentName = alignment.charAt(0).toUpperCase() + alignment.slice(1);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					source: `${alignment}AlignmentButton`,
					actionSubject: 'button',
					actionSubjectId: `pageTitle${alignmentName}AlignButton`,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
	const onAlignmentButtonClick = async (alignment: TitleAlignmentType) => {
		if (alignmentButtonContainer.state.alignmentButtonState === alignment || isUpdatingAlignment) {
			// don't mutate state since it's the current state or it's already trying to mutate the state
			return;
		}
		void alignmentButtonContainer.setIsUpdatingAlignment(true);
		void alignmentButtonContainer.toggleAlignmentButton(alignment);
		await updatePageTitleProperty(JSON.stringify({ titleLayoutAlignment: alignment }));
		void alignmentButtonContainer.setIsUpdatingAlignment(false);
	};
	if (fg('confluence_frontend_title_hover_menu')) {
		return (
			<div css={dropdownMenuStyles}>
				<DropdownMenu<HTMLButtonElement>
					trigger={({ triggerRef, ...props }) => (
						<>
							<Button
								{...props}
								iconBefore={
									<Inline space="space.075">
										{alignmentButtonContainer.state.alignmentButtonState ===
										TitleAlignmentType.LEFT ? (
											<AlignTextLeftIcon
												label={intl.formatMessage(i18n.dropdownMenuLeftAlignedIconLabel)}
											/>
										) : (
											<AlignTextCenterIcon
												label={intl.formatMessage(i18n.dropdownMenuCenterAlignedIconLabel)}
											/>
										)}
										<ChevronDownIcon label="" size="small" />
									</Inline>
								}
								ref={triggerRef}
								label={intl.formatMessage(i18n.dropdownMenuAlignmentMenuLabel)}
								testId={`${
									alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
										? 'left'
										: 'center'
								}-alignment-dropdown-menu`}
								appearance="subtle"
								shouldFitContainer
							/>
						</>
					)}
					spacing="compact"
					onOpenChange={() => setIsAlignmentDropdownOpen(!isAlignmentDropdownOpen)}
					shouldRenderToParent={fg('should-render-to-parent-should-be-truepage-modes')}
				>
					<DropdownItemGroup>
						<Flex direction="row" columnGap="space.025" xcss={dropdownContainerStyles}>
							<IconStyles
								isSelected={
									alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
								}
							>
								<DropdownItem
									onClick={() => {
										createAlignmentButtonAnalytics(TitleAlignmentType.LEFT.toLowerCase());
										void onAlignmentButtonClick(TitleAlignmentType.LEFT);
									}}
									isDisabled={userOffline || alignmentButtonContainer.state.isUpdatingAlignment}
									isSelected={
										alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
									}
								>
									<AlignTextLeftIcon
										label={intl.formatMessage(i18n.leftAlignmentIconLabel)}
										testId="left-alignment-button"
									/>
								</DropdownItem>
							</IconStyles>
							<IconStyles
								isSelected={
									alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER
								}
							>
								<DropdownItem
									onClick={() => {
										createAlignmentButtonAnalytics(TitleAlignmentType.CENTER.toLowerCase());
										void onAlignmentButtonClick(TitleAlignmentType.CENTER);
									}}
									isDisabled={userOffline || alignmentButtonContainer.state.isUpdatingAlignment}
									isSelected={
										alignmentButtonContainer.state.alignmentButtonState ===
										TitleAlignmentType.CENTER
									}
								>
									<AlignTextCenterIcon
										label={intl.formatMessage(i18n.centerAlignmentIconLabel)}
										testId="center-alignment-button"
									/>
								</DropdownItem>
							</IconStyles>
						</Flex>
					</DropdownItemGroup>
				</DropdownMenu>
			</div>
		);
	} else {
		return (
			<>
				<Box
					xcss={leftAlignmentsButtonBoxXcss}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={leftPositionAtEmojiButtonInlineCss}
					testId="left-alignment-button-wrapper"
				>
					<AlignmentButton
						setIsMoreOptionsButtonFocused={setIsMoreOptionsButtonFocused}
						isDisabled={alignmentButtonContainer.state.isUpdatingAlignment}
						floatingToolbarButtonStyle={floatingToolbarButtonStyle}
						userOffline={userOffline}
						handleClick={() => {
							createAlignmentButtonAnalytics(TitleAlignmentType.LEFT.toLowerCase());
							void onAlignmentButtonClick(TitleAlignmentType.LEFT);
						}}
						isSelected={
							alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.LEFT
						}
						icon={
							<AlignTextLeftIcon
								label={intl.formatMessage(i18n.leftAlignmentIconLabel)}
								spacing="spacious"
							/>
						}
					/>
				</Box>
				<Box
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={leftPositionAtEmojiButtonInlineCss}
					testId="center-alignment-button-wrapper"
				>
					<AlignmentButton
						setIsMoreOptionsButtonFocused={setIsMoreOptionsButtonFocused}
						isDisabled={alignmentButtonContainer.state.isUpdatingAlignment}
						floatingToolbarButtonStyle={floatingToolbarButtonStyle}
						userOffline={userOffline}
						handleClick={() => {
							createAlignmentButtonAnalytics(TitleAlignmentType.CENTER.toLowerCase());
							void onAlignmentButtonClick(TitleAlignmentType.CENTER);
						}}
						isSelected={
							alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER
						}
						icon={
							<AlignTextCenterIcon
								label={intl.formatMessage(i18n.centerAlignmentIconLabel)}
								spacing="spacious"
							/>
						}
					/>
				</Box>
				<VerticalLineWrapper>
					<VerticalLine />
				</VerticalLineWrapper>
			</>
		);
	}
};
